import React from "react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useStockOverview } from "../../hooks/useStockOverview";
import { useTheme } from "@mui/material";
import clsx from "clsx";
import styles from "./StockOverview.module.scss";
import { useTranslation } from "next-i18next";

const GAUGE_CX = 105;
const GAUGE_CY = 100;
const GAUGE_INNER_R = 88;
const GAUGE_OUTER_R = 100;
const NEEDLE_BASE_R = 5;
const NEEDLE_LEN = 72;

const GAUGE_SECTORS = [
    { value: 40, color: "#F25555" },
    { value: 40, color: "#F25555" },
    { value: 40, color: "url(#segmentGradient)" },
    { value: 40, color: "#16BB6F" },
    { value: 40, color: "#16BB6F" },
];

const calcAngle = (low: number, target: number, high: number) => {
    if (high === low) return 90;
    const pos = (target - low) / (high - low);
    const deg = pos * 180;
    return 180 - Math.round(deg);
};

type GaugeLabelKey = "strongBuy" | "buy" | "neutral" | "sell" | "strongSell";

const getLabelKey = (low: number, target: number, high: number): GaugeLabelKey => {
    const pos = (target - low) / (high - low);
    if (pos >= 0.8) return "strongBuy";
    if (pos >= 0.6) return "buy";
    if (pos >= 0.4) return "neutral";
    if (pos >= 0.2) return "sell";
    return "strongSell";
};

const Needle = ({ cx, cy, angle, theme }: { cx: number; cy: number; angle: number; theme: string }) => {
    const color = theme === "dark" ? "#d9d9d9" : "#343841";

    return (
        <g>
            <circle cx={cx} cy={cy} r={NEEDLE_BASE_R} fill={color} />
            <path
                d={`M${cx},${cy} l${NEEDLE_LEN},0`}
                stroke={color}
                strokeWidth={2}
                fill={color}
                style={{
                    transform: `rotate(-${angle}deg)`,
                    transformOrigin: `${cx}px ${cy}px`,
                }}
            />
        </g>
    );
};

export const StockOverview: React.FC<{ ticker: string }> = ({ ticker }) => {
    const { data, isLoading, error } = useStockOverview({ ticker });
    const { palette } = useTheme();
    const { t } = useTranslation("common");

    if (isLoading || error || !data) return null;

    const { lowPriceTarget: low, highPriceTarget: high, priceTarget: target } = data;
    if ([low, high, target].some(v => typeof v !== "number")) return null;

    const angle = calcAngle(low, target, high);
    const labelKey = getLabelKey(low, target, high);
    const labelText = t(`analytics.analystForecasts.gauges.labels.${labelKey}`);

    return (
        <div className={styles.container}>
            <div className={styles.title}>
                {t("analytics.analystForecasts.stockOverview.title", { ticker })}
            </div>

            <div className={styles.chart}>
                <ResponsiveContainer width="100%" height={225}>
                    <PieChart>
                        <defs>
                            <linearGradient id="segmentGradient">
                                <stop offset="0%" stopColor="#F25555" />
                                <stop offset="100%" stopColor="#16BB6F" />
                            </linearGradient>
                        </defs>

                        <Pie
                            data={GAUGE_SECTORS}
                            dataKey="value"
                            startAngle={180}
                            endAngle={0}
                            cx={GAUGE_CX}
                            cy={GAUGE_CY}
                            innerRadius={GAUGE_INNER_R}
                            outerRadius={GAUGE_OUTER_R}
                            stroke="none"
                            paddingAngle={4}>
                            {GAUGE_SECTORS.map((s, i) => (
                                <Cell key={i} fill={s.color} />
                            ))}
                        </Pie>

                        <Pie
                            data={GAUGE_SECTORS}
                            dataKey="value"
                            startAngle={180}
                            endAngle={0}
                            cx={GAUGE_CX}
                            cy={GAUGE_CY}
                            innerRadius={GAUGE_INNER_R}
                            outerRadius={GAUGE_OUTER_R}
                            stroke="none"
                            fill="none"
                            activeIndex={1}
                            isAnimationActive={false}
                            activeShape={() => (
                                <Needle cx={GAUGE_CX} cy={GAUGE_CY} angle={angle} theme={palette.mode} />
                            )}
                        />
                    </PieChart>
                </ResponsiveContainer>

                <div className={styles.indicators}>
                    <span className={styles.chartLabel}>
                        {t("analytics.analystForecasts.stockOverview.low")}
                        <br /> {low}
                    </span>

                    <span
                        className={clsx(styles.needleLabel, {
                            [styles.sell]: labelKey === "sell" || labelKey === "strongSell",
                            [styles.neutral]: labelKey === "neutral",
                        })}>
                        {labelText.replace(/\n/g, " ")}
                    </span>

                    <span className={styles.chartLabel}>
                        {t("analytics.analystForecasts.stockOverview.high")}
                        <br /> {high}
                    </span>
                </div>
            </div>

            <div className={styles.scoreInfo}>
                <div className={styles.item}>
                    <div className={styles.red}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.stockOverview.low")} <span>{low}</span>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.yellow}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.stockOverview.target")} <span>{target}</span>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.green}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.stockOverview.high")} <span>{high}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
